/**
 * In-app measured jobs: upload, download, export, import.
 * Browser-only side effects (CustomEvent) are gated so Node (Excel email attachments) stays quiet.
 */

export type JobKind = 'upload' | 'download' | 'export' | 'import' | 'other';
export type JobStatus = 'running' | 'done' | 'error';

export interface JobSnapshot {
  id: string;
  kind: JobKind;
  label: string;
  current: number;
  total: number | null;
  startedAt: number;
  stayRequired: boolean;
  requiresOnline: boolean;
  status: JobStatus;
  message?: string;
}

export const JOB_EVENT = 'logistrade_job_progress';

const jobs = new Map<string, JobSnapshot>();
let seq = 0;

function emit(snapshot: JobSnapshot) {
  jobs.set(snapshot.id, snapshot);
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(JOB_EVENT, { detail: snapshot }));
}

export function estimateRemainingMs(
  current: number,
  total: number | null,
  startedAt: number,
  now: number,
): number | null {
  if (total == null || total <= 0 || current <= 0 || current >= total) return null;
  const elapsed = now - startedAt;
  if (elapsed < 400) return null;
  const rate = current / elapsed;
  if (rate <= 0) return null;
  return Math.round((total - current) / rate);
}

export function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) return sec ? `${min}m ${sec}s` : `${min}m`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin ? `${hr}h ${remMin}m` : `${hr}h`;
}

export function jobPercent(job: JobSnapshot): number | null {
  if (job.total == null || job.total <= 0) return null;
  return Math.min(100, Math.round((job.current / job.total) * 100));
}

export interface StartJobOpts {
  id?: string;
  kind: JobKind;
  label: string;
  total?: number | null;
  stayRequired?: boolean;
  requiresOnline?: boolean;
  message?: string;
}

export function startJob(opts: StartJobOpts): string {
  const id = opts.id || `job-${Date.now()}-${++seq}`;
  const snapshot: JobSnapshot = {
    id,
    kind: opts.kind,
    label: opts.label,
    current: 0,
    total: opts.total ?? null,
    startedAt: Date.now(),
    stayRequired: opts.stayRequired !== false,
    requiresOnline: opts.requiresOnline !== false,
    status: 'running',
    message: opts.message,
  };
  emit(snapshot);
  return id;
}

export function updateJob(
  id: string,
  patch: Partial<Pick<JobSnapshot, 'current' | 'total' | 'message' | 'label'>>,
): void {
  const prev = jobs.get(id);
  if (!prev || prev.status !== 'running') return;
  emit({ ...prev, ...patch });
}

export function completeJob(id: string, message?: string): void {
  const prev = jobs.get(id);
  if (!prev) return;
  emit({
    ...prev,
    status: 'done',
    current: prev.total ?? prev.current,
    message: message ?? prev.message,
  });
  if (typeof window !== 'undefined') {
    window.setTimeout(() => {
      jobs.delete(id);
      window.dispatchEvent(new CustomEvent(JOB_EVENT, { detail: { ...prev, id, status: 'done', dismiss: true } }));
    }, 1800);
  } else {
    jobs.delete(id);
  }
}

export function failJob(id: string, message?: string): void {
  const prev = jobs.get(id);
  if (!prev) return;
  emit({ ...prev, status: 'error', message: message || prev.message });
  if (typeof window !== 'undefined') {
    window.setTimeout(() => {
      jobs.delete(id);
      window.dispatchEvent(new CustomEvent(JOB_EVENT, { detail: { ...prev, id, status: 'error', dismiss: true } }));
    }, 5000);
  } else {
    jobs.delete(id);
  }
}

export function yieldToUi(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  return new Promise((resolve) => window.setTimeout(resolve, 0));
}

export async function runMeasuredJob<T>(
  opts: StartJobOpts,
  work: (ctx: { id: string; update: typeof updateJob }) => Promise<T>,
): Promise<T> {
  const id = startJob(opts);
  try {
    const result = await work({ id, update: updateJob });
    completeJob(id);
    return result;
  } catch (err) {
    failJob(id, err instanceof Error ? err.message : undefined);
    throw err;
  }
}

export function runningJobs(): JobSnapshot[] {
  return [...jobs.values()].filter((j) => j.status === 'running');
}

export function hasStayRequiredJob(): boolean {
  return runningJobs().some((j) => j.stayRequired);
}
